import { MapWorkspace } from '@trimble/geospatial';
import { t } from '@trimble/i18n';
import { TreeNodeDefinition } from '@trimble/ui-components/solidjs';
import { getWorkspaceTypeLabel } from '../../side-panel/workspace/workspace-type-label';

interface WorkspaceLayerLookup {
  id: string;
  name: string;
}

export const MAP_WORKSPACES_ROOT_NODE_ID = 'mapWorkspaces';

const LAYER_NODE_ID_PREFIX = 'wslayer';
const LAYER_NODE_ID_SEPARATOR = '::';
const LAYER_NODE_ID_WORKSPACE_INDEX = 1;
const LAYER_NODE_ID_LAYER_INDEX = 2;
const LAYER_NODE_ID_PART_COUNT = 3;

interface WorkspaceLayerNodeIds {
  readonly workspaceId: string;
  readonly layerId: string;
}

const parseWorkspaceLayerNodeId = (
  nodeId: string
): WorkspaceLayerNodeIds | undefined => {
  const parts = nodeId.split(LAYER_NODE_ID_SEPARATOR);
  if (
    parts.length !== LAYER_NODE_ID_PART_COUNT ||
    parts[0] !== LAYER_NODE_ID_PREFIX
  ) {
    return undefined;
  }

  try {
    return {
      workspaceId: decodeURIComponent(parts[LAYER_NODE_ID_WORKSPACE_INDEX]),
      layerId: decodeURIComponent(parts[LAYER_NODE_ID_LAYER_INDEX]),
    };
  } catch {
    return undefined;
  }
};

export interface WorkspaceTreeItem {
  kind: 'workspace' | 'layer';
  workspaceId: string;
  layerId?: string;
}

/** Encodes a layer id + its owning workspace into a unique tree node id. */
export const makeWorkspaceLayerNodeId = (
  workspaceId: string,
  layerId: string
): string =>
  [
    LAYER_NODE_ID_PREFIX,
    encodeURIComponent(workspaceId),
    encodeURIComponent(layerId),
  ].join(LAYER_NODE_ID_SEPARATOR);

/** Layer id from a `wslayer::<workspace>::<layer>` node id. Undefined for any other format or malformed percent-encoding. */
export const getLayerIdFromNodeId = (nodeId: string): string | undefined =>
  parseWorkspaceLayerNodeId(nodeId)?.layerId;

/** Workspace id from a `wslayer::<workspace>::<layer>` node id. Undefined for any other format or malformed percent-encoding. */
export const getWorkspaceIdFromLayerNodeId = (
  nodeId: string
): string | undefined => parseWorkspaceLayerNodeId(nodeId)?.workspaceId;

export const buildWorkspaceNodes = (
  workspaces: readonly MapWorkspace[],
  layers: ReadonlyArray<WorkspaceLayerLookup>,
  options?: { includeRootWhenEmpty?: boolean }
): TreeNodeDefinition<WorkspaceTreeItem>[] => {
  if (workspaces.length === 0 && !options?.includeRootWhenEmpty) {
    return [];
  }

  const layersById = new Map(layers.map((layer) => [layer.id, layer]));

  const nodes: TreeNodeDefinition<WorkspaceTreeItem>[] = [
    {
      id: MAP_WORKSPACES_ROOT_NODE_ID,
      label: t('mobileMapping.workspace.title'),
      labelIcon: 'tc-icon-map',
      isLeaf: false,
    },
  ];

  for (const workspace of workspaces) {
    nodes.push({
      id: workspace.id,
      label: workspace.name || workspace.id,
      parentId: MAP_WORKSPACES_ROOT_NODE_ID,
      isLeaf: false,
      data: { kind: 'workspace', workspaceId: workspace.id },
      visibleContent: () => (
        <div class="flex items-center gap-2 pr-2 text-xs text-base-muted">
          <span>{getWorkspaceTypeLabel(workspace.type)}</span>
          <span aria-hidden="true">·</span>
          <span>
            {t('mobileMapping.workspace.layerCount', {
              count: workspace.layerIds.length,
            })}
          </span>
        </div>
      ),
    });

    for (const layerId of workspace.layerIds) {
      const layer = layersById.get(layerId);
      nodes.push({
        id: makeWorkspaceLayerNodeId(workspace.id, layerId),
        label: layer?.name ?? layerId,
        parentId: workspace.id,
        isLeaf: true,
        data: { kind: 'layer', workspaceId: workspace.id, layerId },
        toggleTooltip: t(
          'mobileMapping.projectData.toggleFeatureLayerVisibility'
        ),
      });
    }
  }

  return nodes;
};
